import { IValidatorFunction } from "@origin-digital/validation";
import { DivisionCode, divisionCodeMap } from "@origin-digital/platform-enums";
import { MOBILE_PATTERN } from "@origin-digital/validation/src/patterns";

export function isPresent<T>(t: T | undefined | null | void): t is T {
  return t !== undefined && t !== null;
}

export const formatMobile = (phone: string) =>
  phone.replace("+61", "0").replace(/(.{4})(.{3})(.{3})/, "$1 $2 $3");

export const formatLandline = (phone: string) =>
  phone.replace("+61", "0").replace(/(.{2})(.{4})(.{4})/, "$1 $2 $3");

export const formatPhone = (phone: string) => {
  const national = phone.replace("+61", "0");
  return MOBILE_PATTERN.test(national)
    ? formatMobile(national)
    : formatLandline(national);
};

export const formatDob = (dob: string) => dob.split("-").reverse().join("/");

/**
 * Determines whether a DOB value represents a "set" date of birth.
 * Treats null, undefined, and empty string as "not set".
 * Any other value (even malformed) is treated as "set" to prevent
 * accidental re-entry of DOB data.
 *
 * Used in field mappings to derive the `editable` flag for DOB (GROOT-267).
 */
export const isDobSet = (dob: string | null | undefined): boolean =>
  dob != null && dob !== "";
export const formatDobApi = (dob: string) => dob.split("/").reverse().join("-");

export const transformPhone = (value: string) => value.replace(/[^0-9]/g, "");
export const transformPhoneToInternational = (value: string) =>
  value.replace("0", "+61");
export const transformPhoneFromInternational = (value: string) =>
  value.replace("+61", "0");
export const transformDate = (date: string) =>
  date
    .replace(/^(\d\d)(\d)$/g, "$1/$2")
    .replace(/^(\d\d\/\d\d)(\d+)$/g, "$1/$2")
    .replace(/[^\d/]/g, "");

export const reduceValidators = (
  value: string,
  validators: IValidatorFunction[]
): string =>
  validators.reduce<string>((errorMessage, validator: IValidatorFunction) => {
    if (errorMessage) {
      return errorMessage;
    }

    return validator(value) || "";
  }, "");

export const toTitleCase = (str: string | null | undefined) =>
  str ? str.toLowerCase().replace(/^(.)|\s+(.)/g, (c) => c.toUpperCase()) : "";

export const toSentenceCase = (str: string | null | undefined) =>
  str ? str[0].toUpperCase() + str.slice(1).toLowerCase() : "";

export enum ESupplyTypes {
  ELECTRICITY = "ELECTRICITY",
  GAS = "GAS",
  LPG = "LPG",
}

export const mapSupplyType = (
  supplyType: string | null
): string | undefined => {
  if (supplyType === null) {
    return undefined;
  }

  if (supplyType === ESupplyTypes.GAS) {
    return divisionCodeMap[DivisionCode.NATURAL_GAS];
  }

  if (Object.keys(DivisionCode).includes(supplyType)) {
    return divisionCodeMap[supplyType as keyof typeof divisionCodeMap];
  }

  return undefined;
};

export const formatAbn = (abn: string) =>
  abn.replace(/(.{2})(.{3})(.{3})(.{3})/g, "$1 $2 $3 $4");
